import { itunesSearchUrl, matchesQuery, parseItunes } from '../../domain/artwork';
import { type Track, trackKey } from '../../domain/icy-parse';
import { artApiUrl } from '../../domain/stream-url';

/**
 * Iskanje ovitka: **iTunes iz brskalnika**, nato `/api/art` (T4 §6).
 *
 * 🔴 **Zakaj iTunes ni v Workerju, čeprav je najboljši vir.** Izmerjeno z
 * `wrangler tail` 2026-07-20: Cloudflarovim Workerjem vrne `403`, trikrat od
 * treh na pravih skladbah, ki jih z domačega omrežja postreže z `200`.
 * Omejevanje teče po IP, izhodni naslovi Workerjev pa so deljeni med vsemi
 * odjemalci Cloudflara. Iz brskalnika gre poizvedba z **uporabnikovega**
 * naslova, kjer te omejitve ni — in za javno objavo je to celo bolje, ker ima
 * vsak uporabnik svojo kvoto namesto ene skupne.
 *
 * ⚠️ **CORS iTunes je nedosleden.** Izmerjeno: pri šestih zaporednih klicih je
 * eden prišel **brez** `Access-Control-Allow-Origin` in ga brskalnik zavrže.
 * Zato ta pot ni zamenjava za `/api/art`, ampak prvi poskus pred njim — in
 * zato je neuspeh tu popolnoma tih.
 *
 * Kar ostane Workerju: Cover Art Archive in Deezer (oba brez uporabne poti iz
 * brskalnika) ter **skupni KV predpomnilnik**, zaradi katerega poslušalci iste
 * postaje plačajo eno samo poizvedbo navzgor (T3 §4).
 */

const ART_TIMEOUT_MS = 8_000;
/** iTunes je tuj strežnik na kritični poti izrisa; krajše kot naš Worker. */
const ITUNES_TIMEOUT_MS = 5_000;
/**
 * Zgornja meja seje. Ovitki so nizi URL-jev; meja obstaja zato, da poslušanje
 * čez noč ne nabira zapisov brez konca, ne zaradi porabe.
 */
const MAX_ENTRIES = 200;

export type LookupArtwork = (track: Track) => Promise<string | null>;

export interface ArtworkOptions {
  base?: string;
  fetchImpl?: typeof fetch;
  /** `false` izklopi iTunes; testi tako preverijo samo pot prek Workerja. */
  useItunes?: boolean;
}

export function createArtworkLookup(options: ArtworkOptions = {}): LookupArtwork {
  const doFetch: typeof fetch = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const useItunes = options.useItunes ?? true;
  const cache = new Map<string, string | null>();

  /** iTunes prek brskalnika; tih neuspeh ob 403, CORS ali izpadu. */
  async function fromItunes(key: string): Promise<string | null> {
    try {
      const response = await doFetch(itunesSearchUrl(key), {
        signal: AbortSignal.timeout(ITUNES_TIMEOUT_MS),
      });
      if (!response.ok) return null;

      const candidate = parseItunes(await response.json());
      // Isto pravilo kot v Workerju in iz istega modula: vir vedno vrne
      // najboljši približek, tudi kadar ta nima nobene zveze s poizvedbo.
      return candidate !== null && matchesQuery(key, candidate) ? candidate.url : null;
    } catch {
      return null;
    }
  }

  /** `/api/art` — Cover Art Archive in Deezer, s skupnim KV predpomnilnikom. */
  async function fromWorker(key: string): Promise<string | null> {
    try {
      const response = await doFetch(artApiUrl(key, options.base), {
        signal: AbortSignal.timeout(ART_TIMEOUT_MS),
      });
      if (!response.ok) return null;

      const body = (await response.json()) as { url: string | null };
      return body.url;
    } catch {
      return null;
    }
  }

  return async (track) => {
    const key = trackKey(track);
    if (key === '') return null;

    const hit = cache.get(key);
    // `null` je veljaven zapis — »te skladbe ni nikjer« se ne poizveduje znova.
    if (hit !== undefined) return hit;

    const url = (useItunes ? await fromItunes(key) : null) ?? (await fromWorker(key));

    if (cache.size >= MAX_ENTRIES) {
      const oldest = cache.keys().next();
      if (!oldest.done) cache.delete(oldest.value);
    }
    cache.set(key, url);
    return url;
  };
}
